import { navigateToApp } from '../../auth/appNavigation';
import { appUrl } from '../../config/appUrl';
import { Coffee, Bookmark, Navigation, Plus, Minus } from 'lucide-react';
import { useEffect, useState, useRef } from 'react';
import type { CSSProperties, KeyboardEvent, PointerEvent, ReactNode } from 'react';
import type { LandingPlace } from './types';
import { formatDistance } from './types';
import { openBeaw } from './StoreButtons';

type MarkerSize = 'xs' | 'sm' | 'md' | 'lg';

const backgroundPositions: ReadonlyArray<{
  className: string;
  order: number;
  size: MarkerSize;
}> = [
  { className: 'home-map-marker--north-west', order: 1, size: 'sm' },
  { className: 'home-map-marker--north-east', order: 2, size: 'md' },
  { className: 'home-map-marker--south-west', order: 3, size: 'xs' },
  { className: 'home-map-marker--south-east', order: 4, size: 'sm' },
] as const;

function markerStyle(order: number) {
  return { '--marker-order': order } as CSSProperties;
}

function hasRewardCampaign(place: LandingPlace) {
  return Boolean(place.has_campaign && place.campaign && Number(place.campaign.reward_points) > 0 && Number(place.campaign.remaining_slots) > 0);
}

function MapBackground() {
  return <img className="home-map-background" src="/assets/beaw-map-background.svg" alt="" aria-hidden="true" draggable="false"/>;
}

function PinShape() {
  return <svg className="home-map-pin__shape" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7Z"/></svg>;
}

function SafePlaceImage({ src, fallback }: { src: string; fallback: ReactNode }) {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [src]);
  if (!src || failed) return fallback;
  return <img src={src} alt="" loading="lazy" decoding="async" referrerPolicy="no-referrer" onError={() => setFailed(true)}/>;
}

function Photo({ place, campaign }: { place: LandingPlace; campaign: boolean }) {
  return <span className="home-map-pin__photo">
    <SafePlaceImage src={place.image_url} fallback={<span className="home-map-pin__fallback"><Coffee/></span>}/>
    {campaign && <span className="sr-only">มีแคมเปญ</span>}
  </span>;
}

function SmallMarker({ place, order, position = '', size = 'sm', interactive = true, onSelect }: { place: LandingPlace; order: number; position?: string; size?: MarkerSize; interactive?: boolean; onSelect: (place: LandingPlace) => void }) {
  const campaign = Boolean(place.has_campaign && place.campaign);
  return <button type="button" className={`home-map-marker home-map-marker--small home-map-marker--size-${size} ${campaign ? 'has-reward' : ''} ${position}`} style={markerStyle(order)} aria-label={`${place.name}${campaign ? ` รับ ${place.campaign!.reward_points} แต้ม` : ''}`} tabIndex={interactive ? 0 : -1} onClick={(event) => { event.stopPropagation(); if (interactive) onSelect(place); }}>
    <span className="home-map-small-pin"><PinShape/><Photo place={place} campaign={campaign}/></span>
    <span className="home-map-marker__label"><strong>{place.name}</strong><small>{formatDistance(place.distance_m)}</small></span>
  </button>;
}

function RewardMarker({ place, interactive = true, questStyle = false, onSelect }: { place: LandingPlace; interactive?: boolean; questStyle?: boolean; onSelect: (place: LandingPlace) => void }) {
  const points = place.campaign?.reward_points || 0;
  if (questStyle) return <button type="button" className="home-map-marker home-map-marker--reward home-map-marker--selected home-map-marker--quest" style={markerStyle(0)} aria-label={`${place.name} รับ ${points} แต้ม`} tabIndex={interactive ? 0 : -1} onClick={(event) => { event.stopPropagation(); if (interactive) onSelect(place); }}>
    <span className="home-map-quest__aura"/><span className="home-map-quest__pin"><Coffee /></span><span className="home-map-quest__label"><small>{place.name}</small><strong>+{points} pts</strong></span>
  </button>;
  return <button type="button" className="home-map-marker home-map-marker--reward home-map-marker--selected" style={markerStyle(0)} aria-label={`${place.name} รับ ${points} แต้ม`} tabIndex={interactive ? 0 : -1} onClick={(event) => { event.stopPropagation(); if (interactive) onSelect(place); }}>
    <span className="home-map-marker__points">+{points} แต้ม</span>
    <span className="home-map-reward-pin"><PinShape/><Photo place={place} campaign/></span>
    <span className="home-map-marker__label"><strong>{place.name}</strong><small>{formatDistance(place.distance_m)}</small></span>
  </button>;
}

function FeaturedPlace({ place, interactive = true, quote = '', onSwipe, onInteract }: { place: LandingPlace; interactive?: boolean; quote?: string; onSwipe?: (direction: number) => void; onInteract: () => void }) {
  const [savedIds, setSavedIds] = useState<string[]>(() => { try { const value = JSON.parse(localStorage.getItem('beaw.landing.saved-places') || '[]'); return Array.isArray(value) ? value : []; } catch { return []; } });
  const saved = savedIds.includes(place.place_id);
  const toggleSaved = () => { const next = saved ? savedIds.filter(id => id !== place.place_id) : [...savedIds, place.place_id]; setSavedIds(next); try { localStorage.setItem('beaw.landing.saved-places', JSON.stringify(next)); } catch { /* Keep this session's selection. */ } };
  const [offset, setOffset] = useState(0);
  const [exiting, setExiting] = useState(false);
  const drag = useRef<{ x: number; y: number; moved: boolean } | undefined>(undefined);
  const suppressClickUntil = useRef(0);
  const timer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);
  const finishDrag = (event: PointerEvent<HTMLElement>, cancelled = false) => {
    const start = drag.current;
    drag.current = undefined;
    if (!start?.moved) return;
    suppressClickUntil.current = Date.now() + 400;
    const dx = event.clientX - start.x;
    if (cancelled || Math.abs(dx) < event.currentTarget.clientWidth * .22) { setOffset(0); return; }
    const direction = dx < 0 ? 1 : -1;
    setExiting(true);
    setOffset(Math.sign(dx) * event.currentTarget.clientWidth * 1.2);
    timer.current = setTimeout(() => {
      onSwipe?.(direction);
      setExiting(false);
      setOffset(0);
    }, window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 0 : 200);
  };
  const openPlace = () => { if (interactive && !exiting && Date.now() >= suppressClickUntil.current) void navigateToApp(appUrl(`/cafes/${encodeURIComponent(place.place_id)}`)); };
  return <section className="home-map-highlight home-map-highlight--swipe" role={interactive ? 'link' : undefined} tabIndex={interactive ? 0 : -1} aria-label={`เข้าร้าน ${place.name}`} style={{ translate: `${offset}px 0`, rotate: `${offset * .015}deg`, transition: exiting ? 'translate 200ms ease-out, rotate 200ms ease-out' : 'none' }}
    onDragStart={event => event.preventDefault()}
    onPointerDown={event => {
      if (!interactive || !onSwipe || exiting || event.button !== 0 || (event.target as HTMLElement).closest('button')) return;
      onInteract();
      drag.current = { x: event.clientX, y: event.clientY, moved: false };
    }}
    onPointerMove={event => {
      const start = drag.current;
      if (!start) return;
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      if (!start.moved && Math.abs(dy) > Math.max(8, Math.abs(dx))) { drag.current = undefined; return; }
      if (!start.moved && Math.abs(dx) > 8) { start.moved = true; event.currentTarget.setPointerCapture(event.pointerId); }
      if (start.moved) setOffset(dx);
    }}
    onPointerUp={event => finishDrag(event)} onPointerCancel={event => finishDrag(event, true)}
    onClick={event => { event.stopPropagation(); if (!(event.target as HTMLElement).closest('button')) openPlace(); }}
    onKeyDown={event => {
      if (event.target !== event.currentTarget || !interactive) return;
      if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); event.stopPropagation(); openPlace(); }
      if (onSwipe && (event.key === 'ArrowLeft' || event.key === 'ArrowRight')) { event.preventDefault(); onInteract(); onSwipe(event.key === 'ArrowLeft' ? 1 : -1); }
    }}>

    <span className="home-map-highlight__image"><SafePlaceImage src={place.image_url} fallback={<Coffee/>}/></span>
    <div className="home-map-highlight__body" aria-live="polite">
      <span className="home-map-highlight__top"><strong>{place.name}</strong>{place.distance_m !== null && Number.isFinite(place.distance_m) && <small>{formatDistance(place.distance_m)}</small>}</span>
      <span className="home-map-highlight__meta">{place.review_count.toLocaleString('th-TH')} รีวิว</span>
      {quote && <p className="home-map-highlight__quote">“{quote}”</p>}
      <div className="home-map-highlight__actions">
        <button className="home-map-save" type="button" aria-label={saved ? 'เลิกบันทึกร้านบนเครื่องนี้' : 'บันทึกร้านบนเครื่องนี้'} aria-pressed={saved} onClick={toggleSaved} disabled={!interactive}><Bookmark size={18} fill={saved ? 'currentColor' : 'none'}/></button>
      </div>
    </div>
  </section>;
}

export function NearbyMap({ places, city, interactive = true, rewardStyle = 'photo', quotes = {}, onLocate }: { quotes?: Record<string, string>; onLocate?: () => void; places: LandingPlace[]; city: string; interactive?: boolean; rewardStyle?: 'photo' | 'quest' }) {
  const [zoom, setZoom] = useState(1);
  const [activePlaceId, setActivePlaceId] = useState<string>();
  const pauseUntil = useRef(0);
  const heroOrder = useRef<string[]>([]);
  const interacting = useRef(false);
  const selectPlace = (place: LandingPlace) => { pauseUntil.current = Date.now() + 20000; setActivePlaceId(place.place_id); };
  const geoLocated = places.filter((place) => Number.isFinite(place.lat) && Number.isFinite(place.lng));
  const measuredNearby = geoLocated.filter((place) => place.distance_m !== null && Number.isFinite(place.distance_m));
  const geographicPool = measuredNearby.length > 0 ? measuredNearby : geoLocated.length > 0 ? geoLocated : places;
  const rewarded = places.filter(hasRewardCampaign);
  const mapPlaces = [...rewarded, ...geographicPool.filter((place) => !rewarded.some((rewardedPlace) => rewardedPlace.place_id === place.place_id))];
  const ranked = [...mapPlaces].sort((a, b) => Number(hasRewardCampaign(b)) - Number(hasRewardCampaign(a)) || Number(b.selected) - Number(a.selected) || (a.distance_m ?? Number.POSITIVE_INFINITY) - (b.distance_m ?? Number.POSITIVE_INFINITY) || a.rank - b.rank || b.review_count - a.review_count);
  const selected = ranked[0];
  const background = ranked.slice(1, 5);
  const activePlace = ranked.slice(0, 5).find((place) => place.place_id === activePlaceId) ?? selected;
  const rotationKey = ranked.slice(0, 5).map(place => place.place_id).join('|');
  useEffect(() => {
    const order = rotationKey ? rotationKey.split('|') : [];
    for (let i = order.length - 1; i > 0; i--) { const j = Math.floor(Math.random() * (i + 1)); [order[i], order[j]] = [order[j], order[i]]; }
    if (!interactive || !order.length) return;
    heroOrder.current = order;
    setActivePlaceId(order[0]);
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    const timer = window.setInterval(() => {
      if (document.hidden || reducedMotion.matches || interacting.current || Date.now() < pauseUntil.current || order.length < 2) return;
      setActivePlaceId(current => order[(order.indexOf(current || '') + 1) % order.length]);
    }, 6000);
    return () => window.clearInterval(timer);
  }, [rotationKey, interactive]);
  const leadToApp = () => openBeaw('map:canvas');
  const handleKeyboard = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); leadToApp(); }
  };

  return <div className={`nearby-map nearby-map--2888 ${interactive ? 'has-pin-relay' : ''}`} onMouseEnter={() => { interacting.current = true; }} onMouseLeave={() => { interacting.current = false; }} onFocusCapture={() => { interacting.current = true; }} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node)) interacting.current = false; }} role={interactive ? 'link' : 'img'} tabIndex={interactive ? 0 : undefined} aria-label={`แผนที่สถานที่ใกล้คุณใน ${city}`} onClick={interactive ? leadToApp : undefined} onKeyDown={interactive ? handleKeyboard : undefined}>
    <div className="home-map-pin-layer" style={{ transform: `scale(${zoom})` }}>
    <MapBackground/>
    {selected && <div className="home-map-selected-wrap">{hasRewardCampaign(selected) ? <RewardMarker onSelect={selectPlace} place={selected} interactive={interactive} questStyle={rewardStyle === 'quest'}/> : <SmallMarker onSelect={selectPlace} place={selected} order={0} size={activePlace?.place_id === selected.place_id ? "lg" : "md"} interactive={interactive}/>}</div>}
    {background.map((place, index) => <SmallMarker onSelect={selectPlace} key={place.place_id} place={place} order={backgroundPositions[index].order} position={backgroundPositions[index].className} size={activePlace?.place_id === place.place_id ? "lg" : backgroundPositions[index].size} interactive={interactive}/>)}
    </div>
    {activePlace && <FeaturedPlace place={activePlace} interactive={interactive} quote={quotes[activePlace.place_id]} onInteract={() => { pauseUntil.current = Date.now() + 20000; }} onSwipe={interactive && ranked.length > 1 ? direction => {
      pauseUntil.current = Date.now() + 20000;
      const order = heroOrder.current;
      if (order.length) setActivePlaceId(order[(order.indexOf(activePlace.place_id) + direction + order.length) % order.length]);
    } : undefined}/>}
    {interactive && <><div className="home-map-controls" onClick={event => event.stopPropagation()}>
      {onLocate && <button type="button" aria-label="ใช้ตำแหน่งของฉัน" onClick={onLocate}><Navigation size={18}/></button>}
      <button type="button" aria-label="ขยายแผนที่" disabled={zoom >= 1.3} onClick={() => setZoom(value => Math.min(1.3, value + .1))}><Plus size={18}/></button>
      <button type="button" aria-label="ย่อแผนที่" disabled={zoom <= .8} onClick={() => setZoom(value => Math.max(.8, value - .1))}><Minus size={18}/></button>
    </div></>}
    <span className="sr-only">แตะเพื่อดูต่อในแอป Beaw</span>
  </div>;
}
